import React,{ useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AxiosInstance } from "axios";
import axios from "axios";
//Support Safe Api Calls
import { useUser } from '../components/UserContext';
import { createApiClient } from "../components/useApiClient";
import { useSafeRequest } from "../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";



export type StatusDataTemplate =
    | { type: 'VALUE'; fields: ['who', 'date', 'value', 'currency'] }
    | { type: 'OUT'; fields: ['to', 'dateFrom', 'dateTo'] }
    | { type: 'FROM'; fields: ['fromWho', 'dateExpected', 'dateToReturn'] }
    | { type: 'NONE'; fields: [] };

export type ArtworkStatusDto = {
    artworkStatusId: number;
    name: string;
    code: string;
    category: string;
    sortOrder: number;
    extendedInfoType: string | null;
};

export type SetStatusRequest = {
    statusCode: string;
    notes?: string | null;
    whenUtc?: string | null;
    additionalData?: Record<string, any>;
    statusName?: string;
};

export type ArtworkCurrentStatusDto = {
    artworkId: number;
    statusCode: string;
    statusName: string;
    statusDate: string; // ISO
    notes?: string | null;
    additionalData?: Record<string, any>;
};

export type ArtworkStatusHistoryItemDto = {
    artworkStatusHistoryId: number;
    artworkId: number;
    statusDate: string; // ISO
    notes?: string | null;
    changedBy?: string | null;
    artworkStatus: { artworkStatusId: number; code: string; name: string };
    statusCode: string;
    statusName?: string;
    additionalData?: Record<string, any>;
};

export type UseArtworkStatusOptions = {
    /** If true, skip API call when statusCode unchanged and no notes provided. */
    noOpIfUnchanged?: boolean;
    /** Number of retry attempts for POST. */
    retry?: number;
    /** On error, should the hook rethrow? Defaults to false. */
    rethrowOnError?: boolean;
};



const messageFrom = (e: any) => {
    if (axios.isAxiosError(e)) {
        return (
            (e.response?.data && (e.response.data.message || e.response.data.error)) ||
            e.response?.statusText ||
            e.message ||
            "Request failed"
        );
    }
    return e?.message || "Unexpected error";
};

export function useArtworkStatus(
    api: AxiosInstance,
    artworkId: number,
    opts: UseArtworkStatusOptions = {}
) {
    const { activeClientId, getAccessToken, user } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const isAuthenticated = useIsAuthenticated();


    const { noOpIfUnchanged = true, retry = 2, rethrowOnError = false } = opts;

    const [statuses, setStatuses] = useState<ArtworkStatusDto[]>([]);
    const [current, setCurrent] = useState<ArtworkCurrentStatusDto | any | null>(null);
    const [history, setHistory] = useState<ArtworkStatusHistoryItemDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>("");

    // *** Setup safe API Calls ***
    const safeApi = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const statusReq = useSafeRequest<any[]>(safeApi, { maxAttempts: 3 });
    const currentReq = useSafeRequest<any[]>(safeApi, { maxAttempts: 3 });
    const historyReq = useSafeRequest<any[]>(safeApi, { maxAttempts: 3 });

    const loadAll = async () => {
        const tasks = [
            statusReq.run({ method: "GET", url: 'artwork-statuses' }),
            currentReq.run({ method: "GET", url: `/artworks/${artworkId}/status` }),
            historyReq.run({ method: "GET", url: `/artworks/${artworkId}/status-history` }),

            //Other data requirements...
        ];

        const [sta, cur, his] = await Promise.allSettled(tasks);

        if (sta.status === "fulfilled") setStatuses(sta.value);
        if (cur.status === "fulfilled") setCurrent(cur.value);
        if (his.status === "fulfilled") setHistory(his.value);
    };

    // Mounted guard to avoid state updates after unmount
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    const safeSet = (fn: () => void) => {
        if (mountedRef.current) fn();
    };


    React.useEffect(() => {
        // Gate: only load when authenticated and client chosen
        if (!isAuthenticated || activeClientId == null) return;

        const getData = async () => {
            loadAll().catch(() => { });
        };

        getData();
        safeSet(() => setLoading(false));
    }, [api, artworkId]);

    const byCode = useMemo(() => {
        const map = new Map<string, ArtworkStatusDto>();
        for (const s of statuses) map.set(s.code, s);
        return map;
    }, [statuses]);

    const updateStatus = useCallback(
        async (req: SetStatusRequest): Promise<ArtworkCurrentStatusDto | null> => {
            setError("");
            
            const unchanged = !!current && req.statusCode === current.statusCode;
            if (noOpIfUnchanged && unchanged && !req.notes) {
                return current;
            }

            setSaving(true);
            const optimisticDate = new Date().toISOString();

            // Optimistic history insert
            const optimisticName = byCode.get(req.statusCode)?.name || req.statusCode;
            safeSet(() => {
                var AdditionalDataJson = req.additionalData != null
                    ? JSON.stringify(req.additionalData.AdditionalData)
                    : null;

                setHistory((h) => [
                    {
                        artworkStatusHistoryId: Math.floor(Math.random() * 1_000_000_000),
                        artworkId,
                        statusDate: optimisticDate,
                        notes: req.notes ?? null,
                        changedBy: user?.displayName,
                        artworkStatus: { artworkStatusId: 0, code: req.statusCode, name: optimisticName },
                        statusCode: req.statusCode,
                        statusName: req.statusName,
                        additionalData: req.additionalData
                    },
                    ...h,
                ]);
            });

            // retry helper
            const attempt = async (n: number): Promise<ArtworkCurrentStatusDto> => {
                try {
                    const res = await api.post<ArtworkCurrentStatusDto>(`/artworks/${artworkId}/status`, req);
                    return res.data;
                } catch (err) {
                    if (axios.isAxiosError(err) && (err.code === "ERR_CANCELED" || err.name === "CanceledError")) {
                        throw err; // bubble cancellation
                    }
                    if (n <= 0) throw err;
                    await new Promise((r) => setTimeout(r, 300 * (2 - n + 1))); // 300ms, 600ms
                    return attempt(n - 1);
                }
            };

            try {
                const updated = await attempt(retry);
                safeSet(() => {
                    setCurrent(updated);
                    // update the top history row with canonical values
                    setHistory((h) =>
                        h.length > 0
                            ? [
                                {
                                    ...h[0], statusDate: updated.statusDate, artworkStatus: { artworkStatusId: 0, code: updated.statusCode, name: updated.statusName },
                                    additionalData: updated.additionalData, statusCode: updated.statusCode, statusName: updated.statusName
                                },
                                ...h.slice(1),
                            ]
                            : h
                    );
                });
                return updated;
            } catch (e: any) {
                // rollback optimistic insert by reloading, but don't claim an error for benign no-op scenarios
                await loadAll();
                const msg = messageFrom(e);
                safeSet(() => setError(msg));
                if (rethrowOnError) throw e;
                return null;
            } finally {
                safeSet(() => setSaving(false));
            }
        },
        [api, artworkId, current, byCode, noOpIfUnchanged, retry, rethrowOnError, loadAll]
    );

    return { statuses, current, history, loading, saving, error, updateStatus, reload: loadAll } as const;
}
